import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  List,
  MenuItem,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { object, string } from "yup";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { sessionService } from "redux-react-session";
import { useNavigate } from "react-router-dom";

import {
  changeNumber,
  dateFormatLabel,
  emailAddress,
  emailAddressPlaceholder,
  firstNameLabel,
  firstNamePlaceholder,
  lastNameLabel,
  lastNamePlaceholder,
  loadingMsg,
  mobile,
  mobileNoPlaceholder,
  noOptionsFound,
  profile,
  profileUpdateSuccessMsg,
  search,
  selectCountry,
  selectDateFormat,
  submit,
} from "../messages";
import { useSession } from "../../hooks/useSession";
import {
  callAPIInterface,
  concatenatedMobileNumber,
  dateFormatOptions,
  fetchCountries,
  isAlphabet,
} from "../constants";
import { Input } from "../input/Input";
import CustomDivider from "../divider/Divider";
import Button from "../button/Button";
import DialogBox from "../dialog/Dialog";
import Text from "../text/Text";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import CommonHeader from "../CommonHeader";
import { showToast } from "../../redux/actions";
import { store } from "../../redux/store";
import CustomDatePicker from "../datepicker";
import Label from "../label/Label";
import CustomSelect from "../customSelect/CustomSelect";

const Profile = () => {
  const { session } = useSession();
  const [isSelectCountryModalOpen, setIsSelectCountryModalOpen] =
    useState(false);
  const [open, setOpen] = useState(false);
  const [isSubmittingLoader, setIsSubmittingLoader] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [countries, setCountries] = useState([]);
  const [currentSession, setCurrentSession] = useState();

  const navigate = useNavigate();

  const filteredCountries = useMemo(() => {
    return countries?.filter((x) =>
      x.short_name?.toLowerCase().includes(searchValue?.toLowerCase())
    );
  }, [countries, searchValue]);

  useEffect(() => {
    if (isSelectCountryModalOpen && !open) {
      setTimeout(() => setOpen(true), 300);
    }
  }, [isSelectCountryModalOpen]);

  useEffect(() => {
    setCurrentSession(session);
  }, [session]);
  const user = currentSession?.user;

  const validationSchema = object({
    first_name: string().required("").matches(isAlphabet).trim(),
    last_name: string().required("").matches(isAlphabet).trim(),
    country: string().required(),
  });

  const getCountries = async () => {
    const countries = await fetchCountries();
    setCountries(countries);
  };

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    setCurrentSession(session);
  }, [session]);

  const formik = useFormik({
    initialValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      country: user?.country || null,
      date_of_birth: user?.date_of_birth
        ? moment(user?.date_of_birth, ["YYYY-MM-DD", moment.ISO_8601])
        : null,
      date_format: user?.date_format || "DD/MM/YYYY",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (val) => {
      setIsSubmitting(true);
      setIsSubmittingLoader(true);
      try {
        const payload = {
          ...val,
          ...(val?.date_of_birth && {
            date_of_birth: moment(val.date_of_birth).format("YYYY-MM-DD"),
          }),
        };
        await callAPIInterface("POST", "/user", JSON.stringify(payload));

        const updatedSession = {
          ...currentSession,
          user: { ...user, ...payload },
        };
        await sessionService.saveSession(updatedSession);
        setCurrentSession(updatedSession);

        store.dispatch(
          showToast({
            isToastOpen: true,
            toastMessage: profileUpdateSuccessMsg,
            toastVariant: "success",
          })
        );
      } catch (error) {
        setIsSubmitting(false);
      } finally {
        setIsSubmittingLoader(false);
        setIsSubmitting(false);
        resetForm();
      }
    },
  });
  const {
    values,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = formik;

  const closeCountryModal = () => {
    setOpen(false);
    setIsSelectCountryModalOpen(false);
  };

  return (
    <>
      <BackdropLoader
        open={isSubmittingLoader}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader
        textAfterIcon={profile}
        icon="backArrow"
        navigation="/settings"
      />
      <Box className="options-container">
        <Box className="profile-wrapper">
          <Box className="module-container">
            <Box className="module-container-content">
              <Input
                fullWidth
                showLabel
                label={firstNameLabel}
                placeholder={firstNamePlaceholder}
                name="first_name"
                value={values.first_name}
                type="name"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.first_name && Boolean(errors.first_name)}
              />
              <Input
                customClass="mt-24"
                fullWidth
                showLabel
                label={lastNameLabel}
                placeholder={lastNamePlaceholder}
                name="last_name"
                value={values.last_name}
                type="name"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.last_name && Boolean(errors.last_name)}
              />
              <Input
                customClass="mt-24"
                fullWidth
                showLabel
                label={emailAddress}
                placeholder={emailAddressPlaceholder}
                name="email"
                value={values.email}
                type="email"
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
              />
              <Box className="dob-date-picker mt-24">
                <Text className="grey-text dob-label" font="medium" size={14}>
                  Date of Birth
                </Text>
                <CustomDatePicker
                  value={values.date_of_birth}
                  setValue={(val) => setFieldValue("date_of_birth", val)}
                  placeholder={"Select date of birth"}
                  disableFuture
                  maxDate={moment().subtract(18, "years")}
                  format={user?.date_format?.toUpperCase()}
                  defaultCalendarMonth={moment().subtract(18, "years")}
                />
              </Box>
              <Box className="date-format-dropdown mt-24">
                <Label>{dateFormatLabel}</Label>
                <CustomSelect
                  disabled={!dateFormatOptions.length}
                  name={dateFormatLabel}
                  displayEmpty
                  customClass="dropdown"
                  value={values.date_format.toUpperCase()}
                  MenuProps={{
                    id: "filter-asset-menu",
                    disableScrollLock: true,
                  }}
                  onChange={(event) => {
                    const {
                      target: { value },
                    } = event;
                    setFieldValue("date_format", value);
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Box component="span">
                          {selectDateFormat.toUpperCase()}
                        </Box>
                      );
                    }
                    return selected;
                  }}
                >
                  {dateFormatOptions.map((option) => (
                    <MenuItem key={option.id} value={option.label}>
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Box>
              {user?.phone_number && (
                <Input
                  customClass="mt-24"
                  fullWidth
                  showLabel
                  label={mobile}
                  placeholder={mobileNoPlaceholder}
                  name="mobile"
                  value={concatenatedMobileNumber(
                    user?.calling_code,
                    " " + user?.phone_number
                  )}
                  type="tel"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  actionLabel={changeNumber}
                  actionLabelClick={() => {
                    navigate("/phone-number-verification", {
                      state: {
                        isFromProfile: true,
                        user: user,
                      },
                    });
                  }}
                  disabled
                />
              )}
            </Box>
            <Box className="module-container-footer">
              <CustomDivider className="settings-divider" />
              <Button
                fullWidth
                variant="contained"
                label={submit}
                onClick={handleSubmit}
                disabled={!(isValid && dirty) || isSubmitting}
                className="settings-button"
              />
            </Box>
            <DialogBox
              isOpenModal={isSelectCountryModalOpen}
              setIsOpenModal={setIsSelectCountryModalOpen}
              modalHeader={selectCountry}
              handleAction={closeCountryModal}
              directionToOpen="up"
            >
              <Box className="country-dropdown">
                <TextField
                  className="auto-complete-country"
                  placeholder={search}
                  name="country"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e?.target?.value)}
                  inputProps={{
                    autoComplete: "off",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment>
                        <SearchIcon sx={{ color: "#848B9E" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        className="cursor-pointer"
                        onClick={() => {
                          setSearchValue("");
                          setFieldValue("country", "");
                        }}
                      >
                        <ClearIcon sx={{ color: "#848B9E" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Card
                  className="country-list-container"
                  sx={{
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: "0 !important" }}>
                    {filteredCountries?.length > 0 ? (
                      <List sx={{ p: "0 !important" }}>
                        {filteredCountries?.map((option) => {
                          return (
                            <Box
                              key={option.id}
                              sx={{
                                p: "8px 0px",
                                height: "24px",
                                display: "flex",
                                width: "100%",
                                justifyContent: "space-between",
                              }}
                              className="cursor-pointer"
                              onClick={() => {
                                setSearchValue(
                                  option && typeof option === "object"
                                    ? option.short_name
                                    : option
                                );
                                setFieldValue(
                                  "country",
                                  option && typeof option === "object"
                                    ? option.short_name
                                    : option
                                );
                                closeCountryModal();
                              }}
                            >
                              <Text
                                sx={{ color: "#101828" }}
                                font="semibold"
                                size={16}
                              >
                                {option?.short_name}
                              </Text>
                              <Text
                                sx={{ color: "#101828" }}
                                font="semibold"
                                size={16}
                              >
                                {option?.iso2}
                              </Text>
                            </Box>
                          );
                        })}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          p: "8px 0px",
                          height: "32px",
                          width: "100%",
                        }}
                      >
                        <Text
                          sx={{ color: "#101828" }}
                          font="semibold"
                          size={16}
                        >
                          {noOptionsFound}
                        </Text>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </DialogBox>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Profile;
